import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { BarChart3, Trash2, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

import API from '../api_config';

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function SalesPage() {
  const [sales, setSales] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sales`);
      setSales(response.data);
    } catch (error) {
      toast.error('Error al cargar ventas');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const searchSales = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());
      if (paymentMethod) params.append('payment_method', paymentMethod);
      if (minAmount) params.append('min_amount', minAmount);

      const response = await axios.get(`${API}/search/sales?${params.toString()}`);
      setSales(response.data);
    } catch (error) {
      toast.error('Error al buscar ventas');
    }
  }, [startDate, endDate, paymentMethod, minAmount]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (startDate || endDate || paymentMethod || minAmount) {
        searchSales();
      } else {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [startDate, endDate, paymentMethod, minAmount, searchSales, fetchData]);

  const handleDownloadPDF = async (sale) => {
      try {
          const response = await axios.get(`${API}/sales/${sale.id}/pdf`, {
              responseType: 'blob', // Important for binary data
          });
          
          // Create a Blob from the PDF Stream
          const file = new Blob([response.data], { type: 'application/pdf' });
          
          // Build a URL for the Blob
          const fileURL = URL.createObjectURL(file);
          
          // Open the URL on new Window
          window.open(fileURL, '_blank');
      } catch (error) {
          console.error("Download PDF Error", error);
          toast.error('Error al descargar PDF. Verifique permisos.');
      }
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;
    
    try {
        await axios.delete(`${API}/sales/${saleToDelete.id}`);
        toast.success('Venta eliminada y stock revertido');
        setSales(sales.filter(s => s.id !== saleToDelete.id));
        setSaleToDelete(null);
    } catch (error) {
        toast.error('Error al eliminar venta');
    }
  };

  if (!sales) return null;

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Ventas</h1>
        <p className="text-muted-foreground mt-2">Historial de ventas realizadas</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm mb-2">Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label className="text-sm mb-2">Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            <div>
              <Label className="text-sm mb-2">Método de Pago</Label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full h-10 rounded-lg border border-input bg-transparent px-3 py-2 text-sm"
                data-testid="payment-method-filter"
              >
                <option value="">Todos</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta de Crédito</option>
                <option value="transferencia">Transferencia</option>
                <option value="fiado">Crédito</option>
              </select>
            </div>
            <div>
              <Label className="text-sm mb-2">Monto Mínimo</Label>
              <Input
                type="number"
                placeholder="Ej: 10000"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                data-testid="min-amount-input"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex gap-2 items-center">
              <p className="text-sm text-muted-foreground mr-2">
                {sales.length} venta(s) encontrada(s)
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (startDate) params.append('start_date', new Date(startDate).toISOString());
                    if (endDate) params.append('end_date', new Date(endDate).toISOString());
                    if (paymentMethod) params.append('payment_method', paymentMethod);
                    if (minAmount) params.append('min_amount', minAmount);
                    
                    const response = await axios.get(`${API}/sales/export/excel?${params.toString()}`, {
                      responseType: 'blob'
                    });
                    
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `ventas_${new Date().toISOString().split('T')[0]}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (error) {
                    toast.error('Error al exportar Excel');
                  }
                }}
              >
                <FileText className="h-4 w-4 mr-1 text-green-600" />
                Exportar Excel
              </Button>
            </div>
            {(startDate || endDate || paymentMethod || minAmount) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setPaymentMethod('');
                  setMinAmount('');
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {sales.length === 0 && (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay ventas registradas</p>
            </CardContent>
          </Card>
        )}
        
        {sales.map((sale) => {
          return (
            <Card key={sale.id} className="hover:shadow-md transition-shadow" data-testid={`sale-${sale.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">
                        {sale.document_type === 'factura' ? 'Factura' : 'Boleta'} #{sale.folio || sale.id.slice(0, 8)}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${
                        sale.document_type === 'factura' 
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {sale.document_type || 'boleta'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <p className="text-muted-foreground"><span className="font-medium text-foreground">Productos:</span> {sale.items.length}</p>
                      <p className="text-muted-foreground"><span className="font-medium text-foreground">Cliente:</span> {sale.customer_name || "Cliente Casual"}</p>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <span className="font-medium text-foreground">Pago:</span>
                        <span className="capitalize px-1.5 py-0.5 bg-muted rounded text-xs">
                          {sale.payment_method === 'credito' ? 'Tarjeta Crédito' : 
                           sale.payment_method === 'debito' ? 'Tarjeta Débito' :
                           sale.payment_method === 'fiado' ? 'Crédito' : 
                           sale.payment_method === 'multiple' ? 'Múltiple' : 
                           sale.payment_method}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(sale.created_at).toLocaleString('es-CL')}
                    </p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <p className="font-mono font-bold text-2xl">{formatCLP(sale.total)}</p>
                    <div className="flex gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => handleDownloadPDF(sale)}
                            title="Descargar PDF"
                        >
                            <FileText className="h-4 w-4 mr-1" /> PDF
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            onClick={() => setSelectedSale(sale)}
                            title="Ver Detalle"
                        >
                            <Eye className="h-4 w-4 mr-1" /> Ver
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => setSaleToDelete(sale)}
                            title="Eliminar venta (Revierte Inventario)"
                        >
                            <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                        </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!saleToDelete} onOpenChange={(open) => !open && setSaleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar Venta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la venta permanentemente. 
              <br/><br/>
              <strong>Importante:</strong> Los productos vendidos serán devueltos al inventario automáticamente. Si la venta fue fiada, se descontará de la deuda del cliente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSale} className="bg-red-600 hover:bg-red-700">
              Sí, Eliminar y Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selectedSale} onOpenChange={(open) => !open && setSelectedSale(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de Venta #{selectedSale?.id.slice(0, 8)}</DialogTitle>
            <DialogDescription>
                Fecha: {selectedSale && new Date(selectedSale.created_at).toLocaleString('es-CL')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSale && (
            <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-muted/30 p-3 rounded-lg border">
                    <div>
                        <span className="text-muted-foreground block text-xs uppercase font-bold">Folio / Documento</span>
                        <span className="font-semibold capitalize">{selectedSale.document_type || 'boleta'} #{selectedSale.folio || 'N/A'}</span>
                    </div>
                    <div>
                        <span className="text-muted-foreground block text-xs uppercase font-bold">Cliente</span>
                        <span>{selectedSale.customer_name || "Cliente Casual"}</span>
                    </div>
                     <div>
                        <span className="text-muted-foreground block text-xs uppercase font-bold">Método de Pago</span>
                        <span className="capitalize bg-muted px-2 py-0.5 rounded text-xs">
                          {selectedSale.payment_method === 'multiple' ? 'Múltiple / Mixto' : 
                           selectedSale.payment_method === 'fiado' ? 'Crédito' : 
                           selectedSale.payment_method}
                        </span>
                    </div>
                    <div>
                        <span className="text-muted-foreground block text-xs uppercase font-bold">Fecha / Hora</span>
                        <span className="text-xs">{new Date(selectedSale.created_at).toLocaleString('es-CL')}</span>
                    </div>
                </div>

                {selectedSale.payment_method === 'multiple' && selectedSale.payments && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-100 dark:border-purple-800">
                    <p className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-2 uppercase tracking-wider">Desglose de Pago Mixto</p>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedSale.payments.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-xs items-center bg-card p-2 rounded border shadow-sm">
                          <span className="capitalize font-medium">{p.method === 'fiado' ? 'Crédito' : p.method}</span>
                          <span className="font-mono">{formatCLP(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border rounded-lg overflow-hidden shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b">
                            <tr>
                                <th className="p-3 text-left">Producto</th>
                                <th className="p-3 text-center">Cant.</th>
                                <th className="p-3 text-right">Precio</th>
                                <th className="p-3 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {selectedSale.items.map((item, idx) => (
                                <tr key={idx} className="hover:bg-muted/10 transition-colors">
                                    <td className="p-3">
                                      <p className="font-medium">{item.product_name}</p>
                                      {item.sku && <p className="text-[10px] text-muted-foreground font-mono">{item.sku}</p>}
                                    </td>
                                    <td className="p-3 text-center font-mono">{item.quantity}</td>
                                    <td className="p-3 text-right font-mono">{formatCLP(item.price)}</td>
                                    <td className="p-3 text-right font-mono">{formatCLP(item.subtotal)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {selectedSale.discount_details && selectedSale.discount_details.length > 0 && (
                   <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-100 dark:border-green-800">
                      <p className="text-xs font-bold text-green-700 dark:text-green-400 mb-2 uppercase tracking-wider">Ofertas y Promociones Aplicadas</p>
                      <div className="space-y-1">
                        {selectedSale.discount_details.map((disc, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-green-600 dark:text-green-300">
                            <span>{disc.count > 1 ? `(${disc.count}) ` : ''}{disc.name}</span>
                            <span className="font-mono">-{formatCLP(disc.savings)}</span>
                          </div>
                        ))}
                      </div>
                   </div>
                )}

                {selectedSale.notes && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-lg text-sm italic text-muted-foreground">
                    <span className="font-bold text-yellow-800 dark:text-yellow-400 not-italic mr-2">Notas:</span>
                    {selectedSale.notes}
                  </div>
                )}

                <div className="flex flex-col items-end space-y-1 text-sm border-t pt-2">
                    <div className="flex justify-between w-48">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>{formatCLP(selectedSale.subtotal)}</span>
                    </div>
                    {selectedSale.discount_total > 0 && (
                         <div className="flex justify-between w-48 text-green-600">
                            <span>Descuentos:</span>
                            <span>-{formatCLP(selectedSale.discount_total)}</span>
                        </div>
                    )}
                    {selectedSale.tax > 0 && (
                        <div className="flex justify-between w-48">
                            <span className="text-muted-foreground">IVA:</span>
                            <span>{formatCLP(selectedSale.tax)}</span>
                        </div>
                    )}
                     <div className="flex justify-between w-48 font-bold text-lg pt-1 border-t">
                        <span>Total:</span>
                        <span>{formatCLP(selectedSale.total)}</span>
                    </div>
                </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
